import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Download, Lock, LogOut, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'vidat_admin_pw';

const Admin = () => {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [entries, setEntries] = useState([]);
  const [fetchError, setFetchError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const storedPw = () => sessionStorage.getItem(STORAGE_KEY) || '';

  const fetchEntries = useCallback(async () => {
    const pw = storedPw();
    if (!pw) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { 'X-Admin-Password': pw },
      });
      setEntries(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setLoginError('Sesión caducada. Vuelve a entrar.');
      } else {
        setFetchError('No se pudieron cargar las inscripciones.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (storedPw()) {
      setAuthed(true);
      fetchEntries();
    }
  }, [fetchEntries]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(STORAGE_KEY, password);
      setAuthed(true);
      setPassword('');
      fetchEntries();
    } catch (err) {
      setLoginError(err?.response?.data?.detail || 'Contraseña incorrecta');
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setEntries([]);
  };

  const onDownloadCsv = async () => {
    const pw = storedPw();
    if (!pw) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, {
        headers: { 'X-Admin-Password': pw },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `vidat_waitlist_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFetchError('No se pudo descargar el CSV.');
    } finally {
      setDownloading(false);
    }
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-ivory flex items-center justify-center px-6 pt-28 pb-20" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-white border border-stone-200 p-10">
          <div className="flex items-center gap-3 mb-8">
            <Lock size={18} strokeWidth={1.5} className="text-cyan-vidat" />
            <div className="text-[11px] uppercase tracking-[0.28em] text-cyan-vidat font-medium">
              Panel privado
            </div>
          </div>
          <h1 className="display-md text-cyan-vidat mb-2">VIDAT Admin</h1>
          <p className="text-sm text-stone-500 font-light mb-10">
            Acceso restringido. Introduce la contraseña de administrador para ver las inscripciones.
          </p>
          <form onSubmit={onLogin} className="space-y-8" data-testid="admin-login-form">
            <div>
              <label className="vidat-label">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="vidat-input"
                autoFocus
                data-testid="admin-password"
              />
            </div>
            {loginError && (
              <div className="text-sm text-red-500" data-testid="admin-login-error">{loginError}</div>
            )}
            <button type="submit" className="btn-primary w-full" data-testid="admin-login-submit">
              Entrar
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ivory pt-28 pb-20" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="overline mb-4">VIDAT · Administración</div>
            <h1 className="display-md text-cyan-vidat">Lista de espera</h1>
            <span className="section-rule mt-6"></span>
            <p className="text-sm text-stone-500 font-light mt-6">
              {entries.length} {entries.length === 1 ? 'inscripción registrada' : 'inscripciones registradas'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={fetchEntries}
              disabled={loading}
              className="btn-secondary"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'animate-spin' : ''} /> Actualizar
            </button>
            <button
              onClick={onDownloadCsv}
              disabled={downloading || entries.length === 0}
              className="btn-primary"
              data-testid="admin-download-csv"
            >
              <Download size={14} strokeWidth={1.5} /> {downloading ? 'Descargando…' : 'Descargar CSV'}
            </button>
            <button onClick={onLogout} className="text-sm text-stone-500 hover:text-cyan-vidat transition-colors inline-flex items-center gap-1.5" data-testid="admin-logout">
              <LogOut size={14} strokeWidth={1.5} /> Salir
            </button>
          </div>
        </div>

        {fetchError && (
          <div className="mb-6 text-sm text-red-500" data-testid="admin-fetch-error">{fetchError}</div>
        )}

        <div className="bg-white border border-stone-200 overflow-x-auto" data-testid="admin-entries-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-stone-500 border-b border-stone-200">
                <th className="px-6 py-4 font-medium">Fecha</th>
                <th className="px-6 py-4 font-medium">Nombre</th>
                <th className="px-6 py-4 font-medium">Email</th>
                <th className="px-6 py-4 font-medium">Teléfono</th>
                <th className="px-6 py-4 font-medium">Servicio</th>
                <th className="px-6 py-4 font-medium">Idioma</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && !loading && (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center text-stone-400 text-sm">
                    Todavía no hay inscripciones.
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-stone-100 hover:bg-ivory/40 transition-colors">
                  <td className="px-6 py-4 text-stone-500 font-mono text-xs whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-6 py-4 text-stone-800 font-medium">{e.name}</td>
                  <td className="px-6 py-4 text-cyan-vidat">
                    <a href={`mailto:${e.email}`} className="hover:underline">{e.email}</a>
                  </td>
                  <td className="px-6 py-4 text-stone-600">{e.phone || '—'}</td>
                  <td className="px-6 py-4 text-stone-700">{e.service}</td>
                  <td className="px-6 py-4 text-stone-500 uppercase text-xs tracking-wider">{e.language}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
};

export default Admin;
